import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HiOutlineClipboardDocumentList } from 'react-icons/hi2';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import PageHeader from '../../components/misc/PageHeader';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import Button from '../../components/buttons/Button';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import SearchBar from '../../components/forms/SearchBar';
import SectionHeader from '../../components/misc/SectionHeader';
import formsService from '../../../services/community/formsService';
import { Form } from '../../../models/forms.model';
import { CommunityRole } from '../../../types/community';

export default function FormsOverviewPage(): JSX.Element {
  const { t } = useTranslation();
  const community = useSelectedCommunity();
  const navigate = useNavigate();
  const [forms, setForms] = useState<Form[]>([]);
  const [search, setSearch] = useState<string>('');
  const filteredForms = forms.filter((f) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (f.title ?? '').toLowerCase().includes(q) || (f.description ?? '').toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    if (!community) return;
    formsService
      .list()
      .then((data) => setForms(data))
      .catch(() => setForms([]));
  }, [community]);

  if (!community) return <></>;

  const canCreate = community.role === CommunityRole.ADMIN && !!community.features?.FORMS;
  const createButton = canCreate ? (
    <PrimaryButton text={t('page.forms.create')} onClick={() => navigate('/forms/create')} />
  ) : undefined;

  if (!community.features?.FORMS) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t('page.forms.title')} subtitle={t('page.forms.emptyText')} />
        <EmptyScreenView
          title={t('page.forms.emptyTitle')}
          subtitle={t('page.forms.featureDisabled', {
            defaultValue: t('page.forms.emptyText'),
          })}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('page.forms.title')} primaryButton={createButton} />

      <div className="overflow-hidden rounded-[12px] border border-secondary-200 bg-surface">
        <SectionHeader
          title={t('page.forms.title')}
          count={filteredForms.length}
          action={
            <SearchBar
              search={search}
              setSearch={setSearch}
              className="max-w-xs"
              inputProps={{ placeholder: t('page.forms.searchPlaceholder') ?? t('general.search') }}
            />
          }
        />

        <div className="p-4">
          {filteredForms.length === 0 ? (
            <EmptyScreenView
              title={
                search
                  ? (t('page.forms.emptyNoResults') ?? t('page.forms.emptyTitle'))
                  : t('page.forms.emptyTitle')
              }
              subtitle={
                search
                  ? (t('page.forms.emptyTrySearch') ?? t('page.forms.emptyText'))
                  : t('page.forms.emptyText')
              }
              primaryButton={createButton}
            />
          ) : (
            <div className="flex flex-col gap-2">
              {filteredForms.map((f) => (
                <div
                  key={f.id}
                  className="flex flex-col gap-3 rounded-[8px] bg-secondary-200/70 p-4 transition-colors hover:bg-secondary-200 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[8px] bg-primary-100 text-primary-900">
                      <HiOutlineClipboardDocumentList className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-lg font-semibold text-primary-900">
                        {f.title}
                      </div>
                      {f.description && (
                        <div className="mt-0.5 line-clamp-2 text-sm text-gray-500">
                          {f.description}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                    <div className="text-center">
                      <div className="text-xl font-bold text-primary-900">
                        {f._count?.responses ?? f.responses?.length ?? 0}
                      </div>
                      <div className="text-xs text-gray-500">
                        {t('component.formResponses.heading')}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-primary-900">
                        {f._count?.questions ?? f.questions?.length ?? 0}
                      </div>
                      <div className="text-xs text-gray-500">
                        {t('component.formResponses.questions')}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {community.role === CommunityRole.ADMIN && (
                        <Button
                          variant="primary-outline"
                          onClick={() => navigate(`/forms/${f.id}/edit`)}>
                          {t('edit')}
                        </Button>
                      )}
                      {(!f.responsesHidden || community.role === CommunityRole.ADMIN) && (
                        <PrimaryButton
                          text={t('page.forms.seeResponses') ?? t('general.view')}
                          onClick={() => navigate(`/forms/${f.id}`)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
